import React, { useRef, useEffect } from "react";
import SwiperCore from "swiper";
import { Navigation, Pagination, Mousewheel } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { ErrorField } from "../ErrorField/ErrorField";
import { _bemHelper, bemErrorHelper } from "../FieldHelpers";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./ManagedCarouselImagePicker.scss";
import "../Select/ManagedSelect.scss";
import { useT } from "i18n";

interface MangedCarouselImagePickerProps<T> {
  isValid: boolean;
  label?: string;
  value?: any | null;
  displayText: (value: T) => string | undefined;
  onChange: (value: T | undefined) => void;
  namespace?: string;
  options: T[];
  imageUrl: (option: T) => string;
  errorMessage?: string | null;
  disabled?: boolean;
  isItemSelected: (value: any | undefined, option: T) => boolean;
  itemDataCy: (value: T) => string;
  showSearch?: boolean;
}

SwiperCore.use([Navigation, Pagination, Mousewheel]);

export const ManagedCarouselImagePicker = (
  props: MangedCarouselImagePickerProps<any>,
) => {
  const t = useT();
  const [searchTerm, setSearchTerm] = React.useState("");
  const swiperRef = useRef<SwiperCore>();
  // BEM css convention namespace
  const BEM_NS = props.namespace ?? "carousel-image-picker";

  const selectedClassName = _bemHelper(`${BEM_NS}__img`, "selected");
  const defaultClassName = bemErrorHelper(`${BEM_NS}__img`);

  const defaultImageWrapperClassName = bemErrorHelper(`${BEM_NS}__img-label-wrap`);
  const selectedImageWrapperClassName = _bemHelper(
    `${BEM_NS}__img-label-wrap`,
    "selected",
  );

  const getInitalSlide = () => {
    if (!props.value) return 0;
    for (const { index, option } of props.options.map((option, index) => ({
      index,
      option,
    }))) {
      if (props.isItemSelected(props.value, option)) {
        return index;
      }
    }
    return 0;
  };

  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return props.options;
    return props.options.filter((option) =>
      props.displayText(option)?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [props.options, searchTerm, props.displayText]);

  useEffect(() => {
    if (swiperRef.current && props.value) {
      const selectedIndex = filteredOptions.findIndex((option) =>
        props.isItemSelected(props.value, option),
      );
      if (selectedIndex !== -1) {
        swiperRef.current.slideTo(selectedIndex);
      }
    }
  }, [props.value, props.isItemSelected, filteredOptions]);

  return (
    <div className={bemErrorHelper(`${BEM_NS}__wrap`, props.isValid)}>
      <label className={bemErrorHelper(BEM_NS, props.isValid)}>
        <span className={bemErrorHelper(`${BEM_NS}__copy`, props.isValid)}>
          {props.label}
        </span>
        <ErrorField
          className={"margin-bottom"}
          errorMessage={props.errorMessage}
          valid={props.isValid}
        />
        {props.showSearch && !props.disabled && (
          <input
            type={"text"}
            placeholder={`Search ${props.label}s`}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            style={{
              width: "100%",
              padding: "8px 12px",
              marginBottom: "12px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        )}
        {filteredOptions && filteredOptions.length > 0 ? (
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            navigation={true}
            slidesPerView={3}
            spaceBetween={20}
            initialSlide={getInitalSlide()}
            allowTouchMove={false}
            mousewheel={{
              invert: false,
            }}
          >
            {filteredOptions.map((option, key) => {
              return (
                <SwiperSlide
                  key={key}
                  onClick={(e) => {
                    if (!props?.disabled) props.onChange(option);
                  }}
                >
                  <div className={bemErrorHelper(`${BEM_NS}__img-wrap`)}>
                    <div
                      className={
                        props.isItemSelected(props.value, option)
                          ? selectedImageWrapperClassName
                          : defaultImageWrapperClassName
                      }
                      data-cy={props.itemDataCy(option)}
                    >
                      <img
                        className={
                          props.isItemSelected(props.value, option)
                            ? selectedClassName
                            : defaultClassName
                        }
                        alt={props.displayText(option)}
                        src={props.imageUrl(option)}
                      />
                    </div>
                    <span className={bemErrorHelper(`${BEM_NS}__label`)}>
                      {props.displayText(option)}
                    </span>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        ) : (
          <div
            style={{
              height: "203px",
              width: "461px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              margin: "0 auto",
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
              color: "#666",
            }}
          >
            {t("component.imagePicker.noResultsFound", "No results found")}
          </div>
        )}
      </label>
    </div>
  );
};
